export default class ImageZoom {
    private scale: number = 1;
    private minScale: number = 0.5;
    private maxScale: number = 3;
    private scaleStep: number = 0.25;
    private isDragging: boolean = false;
    private startX: number = 0;
    private startY: number = 0;
    private translateX: number = 0;
    private translateY: number = 0;

    private img!: HTMLImageElement;
    private container!: HTMLElement;
    private zoomLevel!: HTMLElement;
    private zoomInBtn!: HTMLButtonElement;
    private zoomOutBtn!: HTMLButtonElement;
    private resetBtn!: HTMLButtonElement;
    private flipBtn: HTMLButtonElement | null = null;
    private isFlippedRef: any;
    private hasFlippedImage: boolean = false;

    // Store bound functions for proper cleanup
    private boundZoomIn!: () => void;
    private boundZoomOut!: () => void;
    private boundResetZoom!: () => void;
    private boundToggleFlip!: () => void;
    private boundStartDrag!: (e: MouseEvent) => void;
    private boundDrag!: (e: MouseEvent) => void;
    private boundStopDrag!: () => void;
    private boundModalShow!: () => void;

    constructor(isFlippedRef?: any, floorPlanFlippedImg?: any) {
        this.isFlippedRef = isFlippedRef;
        this.hasFlippedImage = !!floorPlanFlippedImg;
        this.init();
    }

    private init(): void {
        this.img = document.getElementById('modal-img') as HTMLImageElement;
        this.container = document.getElementById('image-container') as HTMLElement;
        this.zoomLevel = document.getElementById('zoom-level') as HTMLElement;
        this.zoomInBtn = document.getElementById('zoom-in') as HTMLButtonElement;
        this.zoomOutBtn = document.getElementById('zoom-out') as HTMLButtonElement;
        this.resetBtn = document.getElementById('zoom-reset') as HTMLButtonElement;
        this.flipBtn = document.getElementById('flip-plan') as HTMLButtonElement | null;

        this.bindEvents();
        this.updateZoomLevel();
    }

    private bindEvents(): void {
        // Create bound functions
        this.boundZoomIn = () => this.zoomIn();
        this.boundZoomOut = () => this.zoomOut();
        this.boundResetZoom = () => this.resetZoom();
        this.boundToggleFlip = () => this.toggleFlip();
        this.boundStartDrag = (e: MouseEvent) => this.startDrag(e);
        this.boundDrag = (e: MouseEvent) => this.drag(e);
        this.boundStopDrag = () => this.stopDrag();
        this.boundModalShow = () => {
            this.resetZoom();
            this.rebindFlipButton();
            this.updateFlipButton();
        };

        // Zoom controls
        this.zoomInBtn.addEventListener('click', this.boundZoomIn);
        this.zoomOutBtn.addEventListener('click', this.boundZoomOut);
        this.resetBtn.addEventListener('click', this.boundResetZoom);

        // Bind flip button
        this.bindFlipButton();

        // Drag functionality
        this.img.addEventListener('mousedown', this.boundStartDrag);
        document.addEventListener('mousemove', this.boundDrag);
        document.addEventListener('mouseup', this.boundStopDrag);

        // Reset on modal show
        document.getElementById('floor-plan-modal')?.addEventListener('shown.bs.modal', this.boundModalShow);
    }

    private bindFlipButton(): void {
        // Re-query the flip button from DOM
        this.flipBtn = document.getElementById('flip-plan') as HTMLButtonElement | null;

        // Bind if button exists and we have a flipped image
        if (this.flipBtn && this.hasFlippedImage) {
            this.flipBtn.addEventListener('click', this.boundToggleFlip);
        }
    }

    private rebindFlipButton(): void {
        // Remove old listener if it exists
        if (this.flipBtn && this.hasFlippedImage) {
            this.flipBtn.removeEventListener('click', this.boundToggleFlip);
        }

        // Re-bind with fresh DOM reference
        this.bindFlipButton();
    }

    public destroy(): void {
        // Remove all event listeners
        if (this.zoomInBtn) {
            this.zoomInBtn.removeEventListener('click', this.boundZoomIn);
        }
        if (this.zoomOutBtn) {
            this.zoomOutBtn.removeEventListener('click', this.boundZoomOut);
        }
        if (this.resetBtn) {
            this.resetBtn.removeEventListener('click', this.boundResetZoom);
        }
        if (this.flipBtn && this.hasFlippedImage) {
            this.flipBtn.removeEventListener('click', this.boundToggleFlip);
        }
        if (this.img) {
            this.img.removeEventListener('mousedown', this.boundStartDrag);
        }
        document.removeEventListener('mousemove', this.boundDrag);
        document.removeEventListener('mouseup', this.boundStopDrag);

        const modal = document.getElementById('floor-plan-modal');
        if (modal) {
            modal.removeEventListener('shown.bs.modal', this.boundModalShow);
        }
    }

    private toggleFlip(): void {
        if (this.isFlippedRef) {
            this.isFlippedRef.value = !this.isFlippedRef.value;
            this.resetZoom();
            this.updateFlipButton();
        }
    }

    private updateFlipButton(): void {
        if (this.flipBtn && this.isFlippedRef) {
            if (this.isFlippedRef.value) {
                this.flipBtn.classList.add('active');
            } else {
                this.flipBtn.classList.remove('active');
            }
        }
    }

    public zoomIn(centerX: number | null = null, centerY: number | null = null): void {
        if (this.scale < this.maxScale) {
            this.zoom(this.scale + this.scaleStep, centerX, centerY);
        }
    }

    public zoomOut(centerX: number | null = null, centerY: number | null = null): void {
        if (this.scale > this.minScale) {
            this.zoom(this.scale - this.scaleStep, centerX, centerY);
        }
    }

    private zoom(newScale: number, centerX: number | null = null, centerY: number | null = null): void {
        const oldScale = this.scale;
        this.scale = Math.max(this.minScale, Math.min(this.maxScale, newScale));

        if (centerX !== null && centerY !== null) {
            // Zoom towards cursor position
            const scaleDiff = this.scale - oldScale;
            const rect = this.container.getBoundingClientRect();
            const imgRect = this.img.getBoundingClientRect();
            
            const relativeX = (centerX - (imgRect.left - rect.left)) / oldScale;
            const relativeY = (centerY - (imgRect.top - rect.top)) / oldScale;
            
            this.translateX -= relativeX * scaleDiff;
            this.translateY -= relativeY * scaleDiff;
        }

        this.updateTransform();
        this.updateZoomLevel();
    }

    public resetZoom(): void {
        this.scale = 1;
        this.translateX = 0;
        this.translateY = 0;
        this.img.classList.add('fit-container');
        this.updateTransform();
        this.updateZoomLevel();
    }

    private updateTransform(): void {
        if (this.scale === 1) {
            this.img.classList.add('fit-container');
            this.img.style.transform = '';
        } else {
            this.img.classList.remove('fit-container');
            this.img.style.transform = `scale(${this.scale}) translate(${this.translateX}px, ${this.translateY}px)`;
        }
    }

    private updateZoomLevel(): void {
        this.zoomLevel.textContent = Math.round(this.scale * 100) + '%';
        this.zoomInBtn.disabled = this.scale >= this.maxScale;
        this.zoomOutBtn.disabled = this.scale <= this.minScale;
    }

    private startDrag(e: MouseEvent): void {
        if (this.scale > 1) {
            this.isDragging = true;
            this.startX = e.clientX - this.translateX;
            this.startY = e.clientY - this.translateY;
            this.img.style.cursor = 'grabbing';
        }
    }

    private drag(e: MouseEvent): void {
        if (this.isDragging) {
            e.preventDefault();
            this.translateX = e.clientX - this.startX;
            this.translateY = e.clientY - this.startY;
            this.updateTransform();
        }
    }

    private stopDrag(): void {
        this.isDragging = false;
        this.img.style.cursor = this.scale > 1 ? 'grab' : 'default';
    }
}